<template>
  <div v-if="visible" class="hotel-booking-overlay" @click.self="close">
    <div class="hotel-booking-modal">
      <div class="hotel-booking-modal__header">
        <h3>Book This Hotel</h3>
        <span class="hotel-booking-modal__close" @click="close">&times;</span>
      </div>

      <div class="hotel-booking-modal__body">
        <!-- Hotel Info (readonly) -->
        <div class="hotel-booking-modal__info">
          <div class="hotel-booking-modal__image" v-if="firstImage">
            <img :src="firstImage" :alt="partner.title" />
          </div>
          <div class="hotel-booking-modal__details">
            <h4>{{ partner.title }}</h4>
            <div class="hotel-booking-modal__meta">
              <span v-if="starRating" class="hotel-booking-modal__stars">
                <span v-for="n in starRating" :key="n" class="partner-star filled">&#9733;</span>
              </span>
              <span v-if="locationText"> &middot; {{ locationText }}</span>
            </div>
          </div>
        </div>

        <!-- Booking Form -->
        <form class="hotel-booking-modal__form" @submit.prevent="submit">
          <div class="hotel-booking-modal__field">
            <label for="check_in">Check-in</label>
            <input type="date" id="check_in" v-model="form.start_date" :min="minDate" required />
          </div>
          <div class="hotel-booking-modal__field">
            <label for="check_out">Check-out</label>
            <input type="date" id="check_out" v-model="form.end_date" :min="minCheckout" required />
          </div>
          <div class="hotel-booking-modal__field">
            <label for="guests">Guests</label>
            <select id="guests" v-model="form.guests" required>
              <option v-for="n in 10" :key="n" :value="n">{{ n }} {{ n === 1 ? 'Guest' : 'Guests' }}</option>
            </select>
          </div>
          <div class="hotel-booking-modal__field">
            <label for="comments">Special Requests</label>
            <textarea id="comments" v-model="form.comments" rows="3" maxlength="1000" placeholder="Late check-in, room preference, dietary needs..."></textarea>
          </div>

          <div v-if="errors.length" class="hotel-booking-modal__errors">
            <p v-for="(err, idx) in errors" :key="idx">{{ err }}</p>
          </div>

          <button type="submit" class="hotel-booking-modal__submit" :disabled="submitting">
            {{ submitting ? 'Submitting...' : 'Submit Booking Request' }}
          </button>
        </form>
      </div>

      <!-- Success state -->
      <div v-if="success" class="hotel-booking-modal__success">
        <h4>Booking Request Submitted!</h4>
        <p>Our team will review your request and get back to you shortly.</p>
        <button @click="close" class="hotel-booking-modal__submit">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    partner: { type: Object, required: true }
  },
  data() {
    return {
      visible: false,
      submitting: false,
      success: false,
      errors: [],
      form: {
        start_date: '',
        end_date: '',
        guests: 2,
        comments: ''
      }
    }
  },
  computed: {
    metadata() { return this.partner.metadata || {} },
    starRating() { return this.metadata.hotel_class || null },
    locationText() {
      if (this.partner.city) return this.partner.city.name || this.partner.city.title
      if (this.partner.country) return this.partner.country.name
      return null
    },
    firstImage() {
      if (!Array.isArray(this.partner.images) || !this.partner.images.length) return null
      const img = this.partner.images[0]
      return typeof img === 'string' ? img : (img.medium || img.large || null)
    },
    perks() { return this.metadata.perks || [] },
    perkLines() {
      const lines = []
      this.perks.forEach(perk => {
        if (perk.typical_perks) {
          perk.typical_perks.split('\n').forEach(line => {
            const trimmed = line.replace(/^\*\s*/, '').trim()
            if (trimmed) lines.push(trimmed)
          })
        }
      })
      return lines
    },
    minDate() {
      const d = new Date()
      d.setDate(d.getDate() + 1)
      return d.toISOString().split('T')[0]
    },
    minCheckout() {
      if (!this.form.start_date) return this.minDate
      const d = new Date(this.form.start_date)
      d.setDate(d.getDate() + 1)
      return d.toISOString().split('T')[0]
    }
  },
  mounted() {
    eventBus.$on('open-hotel-booking', () => {
      this.open()
    })
  },
  beforeDestroy() {
    eventBus.$off('open-hotel-booking')
  },
  methods: {
    open() {
      this.visible = true
      this.success = false
      this.errors = []
      this.form = { start_date: '', end_date: '', guests: 2, comments: '' }
    },
    close() {
      this.visible = false
    },
    async submit() {
      this.errors = []
      this.submitting = true

      try {
        const token = document.querySelector('meta[name="csrf-token"]')?.content
        const response = await fetch(`/partner/${this.partner.id}/book`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-TOKEN': token
          },
          body: JSON.stringify(this.form)
        })

        const data = await response.json()

        if (!response.ok) {
          if (response.status === 401) {
            window.location.href = '/login'
            return
          }
          if (data.errors) {
            this.errors = Object.values(data.errors).flat()
          } else {
            this.errors = [data.message || 'Something went wrong. Please try again.']
          }
          return
        }

        this.success = true
      } catch (e) {
        this.errors = ['Network error. Please try again.']
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>
